/**
 * MigrationLogger — structured logging for IDB→SQLite migration.
 *
 * Writes rich diagnostic entries to `debug_log` so users can see
 * exactly what happened during the first-time migration from
 * IndexedDB to SQLite on native devices.
 *
 * Each log entry stores a JSON-encoded `MigrationLogEntry` in the
 * `description` field of `DbLog`. The existing SettingsHelp and
 * SettingsDeveloper views already show these entries — but a
 * dedicated migration log viewer can parse the JSON for richer display.
 *
 * Usage:
 *   const logger = createMigrationLogger(db)
 *   await logger.start("003-idb-to-sqlite", { tables: ["setting_local", ...] })
 *   await logger.tableStart("setting_local", { sourceRows: 42 })
 *   await logger.tableEnd("setting_local", { migratedRows: 42, durationMs: 120 })
 *   await logger.tableError("debug_log", error)
 *   await logger.end({ totalMigrated: 42, totalErrors: 1, durationMs: 450 })
 */

import type { Table } from "dexie"

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Discriminated union of all migration log entry types */
export type MigrationLogEntry =
  | MigrationStartEntry
  | MigrationTableStartEntry
  | MigrationTableEndEntry
  | MigrationTableSkipEntry
  | MigrationTableErrorEntry
  | MigrationEndEntry

interface MigrationStartEntry {
  type: "migration:start"
  migrationId: string
  tables: string[]
  /** Device/platform info if available */
  platform?: string
}

interface MigrationTableStartEntry {
  type: "migration:table:start"
  migrationId: string
  table: string
  sourceRows: number
}

interface MigrationTableEndEntry {
  type: "migration:table:end"
  migrationId: string
  table: string
  migratedRows: number
  durationMs: number
}

interface MigrationTableSkipEntry {
  type: "migration:table:skip"
  migrationId: string
  table: string
  reason: "already-has-data" | "source-empty" | "source-missing"
  existingRows?: number
}

interface MigrationTableErrorEntry {
  type: "migration:table:error"
  migrationId: string
  table: string
  error: string
  durationMs: number
}

interface MigrationEndEntry {
  type: "migration:end"
  migrationId: string
  totalMigrated: number
  totalSkipped: number
  totalErrors: number
  durationMs: number
  tableResults: TableResult[]
}

export interface TableResult {
  table: string
  status: "migrated" | "skipped" | "error"
  rows: number
  durationMs: number
  error?: string
  skipReason?: string
}

// ---------------------------------------------------------------------------
// Logger interface (injectable for testing)
// ---------------------------------------------------------------------------

export interface MigrationLogger {
  start(
    migrationId: string,
    opts: { tables: string[]; platform?: string },
  ): Promise<void>

  tableStart(table: string, opts: { sourceRows: number }): Promise<void>

  tableEnd(
    table: string,
    opts: { migratedRows: number; durationMs: number },
  ): Promise<void>

  tableSkip(
    table: string,
    opts: {
      reason: "already-has-data" | "source-empty" | "source-missing"
      existingRows?: number
    },
  ): Promise<void>

  tableError(table: string, error: unknown, durationMs: number): Promise<void>

  end(opts: {
    totalMigrated: number
    totalSkipped: number
    totalErrors: number
    durationMs: number
    tableResults: TableResult[]
  }): Promise<void>
}

// ---------------------------------------------------------------------------
// DbLog interface (matches schema.ts — redeclared here to avoid circular import)
// ---------------------------------------------------------------------------

interface DbLogRow {
  id?: string
  description: string
  createdAt: string
}

// ---------------------------------------------------------------------------
// Implementation
// ---------------------------------------------------------------------------

/**
 * Create a MigrationLogger that writes to a `debug_log` Dexie table.
 *
 * @param debugLogTable - The `db.debug_log` table (or any Table<DbLog>)
 */
export function createMigrationLogger(
  debugLogTable: Table<DbLogRow, string>,
): MigrationLogger {
  let migrationId = ""
  let seq = 0

  async function write(entry: MigrationLogEntry): Promise<void> {
    const now = new Date().toISOString()
    const prefix = `[${entry.type}]`
    // description contains both a human-readable prefix and the full JSON
    const description = `${prefix} ${JSON.stringify(entry)}`

    try {
      await debugLogTable.add({
        id: `migration-${migrationId}-${String(seq++).padStart(4, "0")}-${Date.now()}`,
        description,
        createdAt: now,
      })
    } catch {
      // Best-effort — if the log table itself is broken, don't crash the migration
      console.warn(`[MigrationLogger] Failed to write log entry:`, entry)
    }
  }

  return {
    async start(id, opts) {
      migrationId = id
      console.info(
        `[MigrationLogger] Starting migration "${id}" — tables: ${opts.tables.join(", ")}`,
      )
      await write({
        type: "migration:start",
        migrationId: id,
        tables: opts.tables,
        platform: opts.platform,
      })
    },

    async tableStart(table, opts) {
      console.info(
        `[MigrationLogger] ${table}: reading ${opts.sourceRows} rows from IDB`,
      )
      await write({
        type: "migration:table:start",
        migrationId,
        table,
        sourceRows: opts.sourceRows,
      })
    },

    async tableEnd(table, opts) {
      console.info(
        `[MigrationLogger] ${table}: migrated ${opts.migratedRows} rows in ${opts.durationMs}ms`,
      )
      await write({
        type: "migration:table:end",
        migrationId,
        table,
        migratedRows: opts.migratedRows,
        durationMs: opts.durationMs,
      })
    },

    async tableSkip(table, opts) {
      console.info(
        `[MigrationLogger] ${table}: skipped (${opts.reason}${opts.existingRows !== undefined ? `, ${opts.existingRows} existing` : ""})`,
      )
      await write({
        type: "migration:table:skip",
        migrationId,
        table,
        reason: opts.reason,
        existingRows: opts.existingRows,
      })
    },

    async tableError(table, error, durationMs) {
      const msg =
        error instanceof Error ? error.message : String(error)
      console.error(
        `[MigrationLogger] ${table}: ERROR after ${durationMs}ms — ${msg}`,
      )
      await write({
        type: "migration:table:error",
        migrationId,
        table,
        error: msg,
        durationMs,
      })
    },

    async end(opts) {
      console.info(
        `[MigrationLogger] Migration "${migrationId}" complete — ` +
          `${opts.totalMigrated} migrated, ${opts.totalSkipped} skipped, ` +
          `${opts.totalErrors} errors in ${opts.durationMs}ms`,
      )
      await write({
        type: "migration:end",
        migrationId,
        totalMigrated: opts.totalMigrated,
        totalSkipped: opts.totalSkipped,
        totalErrors: opts.totalErrors,
        durationMs: opts.durationMs,
        tableResults: opts.tableResults,
      })
    },
  }
}

// ---------------------------------------------------------------------------
// Parsing helper — used by the UI to extract structured data from debug_log
// ---------------------------------------------------------------------------

/**
 * Try to parse a debug_log description as a MigrationLogEntry.
 * Returns null if it's not a migration log entry.
 */
export function parseMigrationLogEntry(
  description: string,
): MigrationLogEntry | null {
  // Format: "[migration:xxx] {json}"
  const match = description.match(/^\[migration:[^\]]+\]\s+(.+)$/)
  if (!match?.[1]) return null
  try {
    const parsed = JSON.parse(match[1])
    if (typeof parsed === "object" && parsed?.type?.startsWith("migration:")) {
      return parsed as MigrationLogEntry
    }
  } catch {
    // Not valid JSON
  }
  return null
}
